import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "../components/marketing/Seo";
import Breadcrumbs from "../components/marketing/Breadcrumbs";
import { IMAGES, SAMPLE_BRIEFINGS } from "../data/marketing";

const API = process.env.REACT_APP_BACKEND_URL;
const FILTERS = ["All", "EPR", "Solid waste & BWGs", "CSR & circularity"];

export const readMinutes = (text) => Math.max(1, Math.round((text || "").split(/\s+/).length / 200));

function publishedCategory(article) {
  if (article.slug.includes("bwg")) return "Solid waste & BWGs";
  if (article.slug.includes("csr")) return "CSR & circularity";
  return "EPR";
}

export default function InsightsPage() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loaded, setLoaded] = useState(false);
  useSeo({ title: "Insights", description: "CLYMACO insights across EPR, solid waste, bulk waste generators, circular economy and waste-focused CSR.", path: "/insights" });

  useEffect(() => {
    fetch(`${API}/api/articles`)
      .then((r) => r.json())
      .then((d) => setArticles((d.items || []).map((a) => ({ ...a, category: publishedCategory(a), author: "CLYMACO", image: IMAGES.hero }))))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const items = useMemo(() => {
    const all = [...SAMPLE_BRIEFINGS, ...articles];
    return filter === "All" ? all : all.filter((a) => a.category === filter);
  }, [articles, filter]);

  return (
    <main id="main" className="bg-parchment-base pt-36" data-testid="insights-page">
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Insights" }]} />
        <div className="max-w-3xl">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">Insights and field notes</div>
          <h1 className="mt-4 font-display text-5xl font-medium tracking-tight text-forest-deep sm:text-6xl" data-testid="insights-title">Ideas that help you make the next decision.</h1>
          <p className="mt-5 text-lg leading-relaxed text-forest-deep/65">Perspectives on regulation, waste systems and circularity. From the obligation on paper to the work it requires.</p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="insights-filters">
          {FILTERS.map((item) => (
            <button key={item} type="button" onClick={() => setFilter(item)} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === item ? "bg-forest-deep text-white" : "border border-forest-deep/15 text-forest-deep hover:bg-white"}`} data-testid={`insights-filter-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {item}
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((article) => (
            <Link key={article.slug} to={`/insights/${article.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-forest-deep/10 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-forest-deep/10" data-testid={`insight-card-${article.slug}`}>
              <div className="relative h-48 bg-forest-deep">
                <img src={article.image || IMAGES.hero} alt={article.draft ? "Illustrative article image" : "CLYMACO editorial image"} className="h-full w-full object-cover opacity-85" loading="lazy" />
                <span className={`absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${article.draft ? "bg-amber-400 text-forest-deep" : "bg-emerald-400 text-forest-deep"}`}>
                  {article.draft ? "Sample briefing" : "Published article"}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-forest-primary">{article.category}</div>
                <h2 className="mt-3 flex-1 font-display text-2xl font-medium leading-snug text-forest-deep">{article.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-forest-deep/60">{article.excerpt}</p>
                <div className="mt-6 flex items-center justify-between border-t border-forest-deep/10 pt-4 text-xs text-forest-deep/50">
                  <span>{article.date || (article.published_at ? new Date(article.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "")}</span>
                  <span className="inline-flex items-center gap-1 font-bold text-forest-primary">Read <ArrowUpRight className="h-3.5 w-3.5" /></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        {loaded && items.length === 0 && <p className="py-16 text-center text-forest-deep/50" data-testid="insights-empty">No articles in this view.</p>}
      </section>
    </main>
  );
}
